import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Plus, Trash2, Upload, X, Heart } from 'lucide-react';
import { memorialTributes } from '../../data/templateCustomizationSchemas';

const defaultPerson = { name: '', relation: '', photo: null, text: '', tributes: [], customTribute: '', visibility: 'private' };

export const MemorialSection = ({ memorials, onChange }) => {
  const [showSection, setShowSection] = useState(memorials.length > 0);

  const addPerson = () => onChange([...memorials, { ...defaultPerson }]);
  const removePerson = (idx) => onChange(memorials.filter((_, i) => i !== idx));
  const updatePerson = (idx, updated) => onChange(memorials.map((m, i) => i === idx ? updated : m));

  if (!showSection) {
    return (
      <div data-testid="memorial-section" className="space-y-4">
        <div className="bg-white rounded-2xl border border-stone-100 p-8 text-center">
          <Heart className="w-8 h-8 text-[#B49A88] mx-auto mb-4" />
          <p className="text-sm font-sans text-stone-700 mb-1.5 max-w-md mx-auto leading-relaxed">
            Is there anyone who cannot be present, that you wish to include or honor in a special way?
          </p>
          <p className="text-xs font-sans text-stone-400 mb-5">Denne seksjonen er helt valgfri.</p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={() => { setShowSection(true); addPerson(); }} variant="outline"
              className="rounded-full font-sans text-sm border-stone-200 hover:border-primary">
              Yes, I wish to honor someone
            </Button>
            <span className="text-xs font-sans text-stone-400">or</span>
            <button onClick={() => {}} className="text-xs font-sans text-stone-500 hover:text-stone-700 underline underline-offset-2">
              Hopp over
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="memorial-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Those we carry with us</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Hedre de som er viktige for dere</p>
        </div>
        <Button type="button" onClick={addPerson} variant="outline" size="sm" className="rounded-full text-xs font-sans gap-1.5 border-stone-200">
          <Plus className="w-3.5 h-3.5" /> Add person
        </Button>
      </div>

      <div className="space-y-4">
        {memorials.map((person, i) => (
          <div key={i} className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
            <div className="flex items-start gap-4">
              {/* Photo */}
              <div className="flex-shrink-0">
                {person.photo ? (
                  <div className="relative w-16 h-16">
                    <img src={person.photo} alt="" className="w-16 h-16 rounded-xl object-cover border border-stone-200" />
                    <button onClick={() => updatePerson(i, { ...person, photo: null })}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ) : (
                  <label className="w-16 h-16 rounded-xl border-2 border-dashed border-stone-200 hover:border-primary/40 flex items-center justify-center cursor-pointer transition-colors">
                    <Upload className="w-5 h-5 text-stone-300" />
                    <input type="file" accept="image/*" className="hidden" onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) { const r = new FileReader(); r.onload = ev => updatePerson(i, { ...person, photo: ev.target.result }); r.readAsDataURL(file); }
                    }} />
                  </label>
                )}
              </div>

              {/* Name & Relation */}
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input value={person.name} onChange={e => updatePerson(i, { ...person, name: e.target.value })} placeholder="Navn"
                    className="text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
                  <input value={person.relation} onChange={e => updatePerson(i, { ...person, relation: e.target.value })} placeholder="Relasjon (e.g. bestemor)"
                    className="text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
                </div>
                <textarea value={person.text} onChange={e => updatePerson(i, { ...person, text: e.target.value })}
                  placeholder="Et kort minne or noen ord (optional)" rows={2}
                  className="w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
              </div>

              <button onClick={() => removePerson(i)} className="text-stone-400 hover:text-red-500 mt-1"><Trash2 className="w-4 h-4" /></button>
            </div>

            {/* Tribute Options */}
            <div>
              <p className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wide mb-2">How do you wish to honor {person.name || 'this person'}?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {memorialTributes.map(tribute => {
                  const isSelected = person.tributes.includes(tribute.id);
                  return (
                    <button key={tribute.id} type="button"
                      onClick={() => {
                        const tributes = isSelected ? person.tributes.filter(t => t !== tribute.id) : [...person.tributes, tribute.id];
                        updatePerson(i, { ...person, tributes });
                      }}
                      className={`text-left text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                        isSelected ? 'bg-primary/5 border-primary/30 text-primary' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
                      }`}>
                      {tribute.label}
                    </button>
                  );
                })}
              </div>
              {person.tributes.includes('custom') && (
                <textarea value={person.customTribute} onChange={e => updatePerson(i, { ...person, customTribute: e.target.value })}
                  placeholder="Beskriv din egendefinerte idé" rows={2}
                  className="mt-2 w-full text-sm font-sans px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
              )}
            </div>

            {/* Visibility */}
            <div className="flex items-center gap-3">
              <p className="text-xs font-sans text-stone-500">Visiblehet:</p>
              {['private', 'visible'].map(opt => (
                <button key={opt} type="button"
                  onClick={() => updatePerson(i, { ...person, visibility: opt })}
                  className={`text-xs font-sans px-3 py-1.5 rounded-full border transition-all ${
                    person.visibility === opt ? 'bg-primary/10 border-primary text-primary' : 'bg-white border-stone-200 text-stone-500'
                  }`}>
                  {opt === 'private' ? 'Private og subtilt' : 'Visible for guests'}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
